import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import api, { imgUrl, inr, durationLabel, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { RateChartDialog } from "@/components/RateChart";
import { OfferCountdown } from "@/components/OfferCountdown";

const CHAPTERS = [
  { n: "01", title: "Finance Tools", text: "Precision-built instruments that track, project and protect your capital across every plan you hold with us." },
  { n: "02", title: "Motion Pictures", text: "Co-invest in curated film productions and earn from box-office, satellite and streaming rights." },
  { n: "03", title: "Bullion & Gold", text: "Allocated 24K physical gold with insured vault custody — the oldest store of value, modernised." },
  { n: "04", title: "Currency Markets", text: "A managed forex desk trading major and exotic pairs under strict drawdown discipline." },
];

const MARQUEE_ITEMS = ["MOVIES", "GOLD", "FOREX", "WEALTH", "CINEMA", "BULLION", "CURRENCY"];

const CONTACT = {
  email: "metrixworks52@gmail.com",
};

const PlanCard = ({ plan, onInvest }) => (
  <div className="group bg-[#0A0A0A] border border-white/10 hover:border-[#D4AF37]/50 transition-colors flex flex-col" data-testid={`plan-card-${plan.category.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="relative aspect-[4/5] overflow-hidden">
      <img
        src={imgUrl(plan.image)}
        alt={plan.title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent" />
      <span className="absolute top-4 left-4 font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] bg-black/60 backdrop-blur-md border border-[#D4AF37]/30 px-3 py-1">
        {plan.category}
      </span>
      <OfferCountdown plan={plan} className="absolute top-12 left-4" />
      <div className="absolute bottom-4 left-4 right-4">
        <h3 className="font-display text-2xl text-white">{plan.title}</h3>
      </div>
    </div>
    <div className="p-6 flex flex-col flex-1">
      <p className="text-sm text-white/50 leading-relaxed line-clamp-3">{plan.description}</p>
      <div className="mt-6 grid grid-cols-2 gap-y-4 border-t border-white/10 pt-6">
        <div>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">Min. Investment</p>
          <p className="text-sm text-white mt-1">{inr(plan.min_investment)}</p>
        </div>
        <div>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">Expected Returns</p>
          <p className="text-sm text-[#D4AF37] mt-1">{plan.expected_return}</p>
        </div>
        <div>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">Duration</p>
          <p className="text-sm text-white mt-1">{durationLabel(plan)}</p>
        </div>
        <div>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">Risk Profile</p>
          <p className="text-sm text-white mt-1">{plan.risk}</p>
        </div>
      </div>
      <div className="mt-auto pt-6 flex gap-3">
        {plan.rate_table?.length > 0 && (
          <RateChartDialog plan={plan}>
            <button
              data-testid={`plan-rate-chart-button-${plan.id}`}
              className="flex-1 border border-white/20 text-white/70 py-3 font-accent text-[10px] tracking-[0.2em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              Rate Chart
            </button>
          </RateChartDialog>
        )}
        <button
          data-testid={`plan-invest-button-${plan.id}`}
          onClick={() => onInvest(plan)}
          className="flex-1 border border-[#D4AF37]/60 text-[#D4AF37] py-3 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors"
        >
          Invest Now
        </button>
      </div>
    </div>
  </div>
);

const EnquiryForm = ({ plans }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", plan_interest: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received. Our team will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", plan_interest: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <input data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
        <input data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={inputCls} />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <input data-testid="enquiry-phone-input" required value={form.phone} onChange={set("phone")} placeholder="Phone number" className={inputCls} />
        <select data-testid="enquiry-plan-select" value={form.plan_interest} onChange={set("plan_interest")} className={`${inputCls} bg-[#050505]`}>
          <option value="">Interested plan (optional)</option>
          {plans.map((p) => (
            <option key={p.id} value={p.title}>{p.title}</option>
          ))}
        </select>
      </div>
      <textarea data-testid="enquiry-message-input" required value={form.message} onChange={set("message")} placeholder="Tell us about your investment goals" rows={4} className={inputCls} />
      <button
        data-testid="enquiry-submit-button"
        disabled={sending}
        className="border border-[#D4AF37] text-[#D4AF37] px-8 py-4 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50"
      >
        {sending ? "Sending…" : "Send Enquiry"}
      </button>
    </form>
  );
};

export default function Home() {
  const [plans, setPlans] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const scrollTo = (hash) => {
    if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -70 });
    else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleInvest = () => navigate(user ? "/dashboard" : "/register");

  return (
    <div className="bg-[#050505] text-white" data-testid="home-page">
      <div className="grain-overlay" />
      <Navbar />
      <Hero onExplore={() => scrollTo("#plans")} onJoin={() => navigate(user ? "/dashboard" : "/register")} />

      <div className="border-y border-white/10 py-8 overflow-hidden" data-testid="editorial-marquee">
        <Marquee speed={25} gradient={false}>
          {MARQUEE_ITEMS.map((item) => (
            <span key={item} className="flex items-center">
              <span className="font-display text-5xl lg:text-6xl text-outline-gold mx-8 tracking-tight">{item}</span>
              <span className="w-2 h-2 bg-[#D4AF37]/60 rotate-45" />
            </span>
          ))}
        </Marquee>
      </div>

      <section id="manifesto" className="max-w-7xl mx-auto px-6 lg:px-12 py-32" data-testid="manifesto-section">
        <Reveal>
          <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-6">The Manifesto</p>
          <h2 className="font-display text-4xl lg:text-5xl tracking-tighter max-w-2xl">Four disciplines. One standard of wealth.</h2>
        </Reveal>
        <div className="mt-24 space-y-0">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className={`grid md:grid-cols-12 gap-8 py-12 border-t border-white/10 ${i % 2 === 1 ? "md:pl-24" : ""}`} data-testid={`manifesto-chapter-${c.n}`}>
                <span className="font-accent text-sm text-[#D4AF37] md:col-span-2">{c.n}</span>
                <h3 className="font-display text-3xl tracking-tight md:col-span-4">{c.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed md:col-span-6 max-w-lg">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="plans" className="max-w-7xl mx-auto px-6 lg:px-12 py-24" data-testid="plans-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-6">The Portfolio</p>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tighter">Investment Plans</h2>
            </div>
            <p className="text-white/40 text-sm max-w-xs">Curated across cinema, bullion and currency markets. Choose the discipline that fits your ambition.</p>
          </div>
        </Reveal>
        {plans.length === 0 ? (
          <p className="text-white/40 font-accent text-xs tracking-[0.2em] uppercase" data-testid="plans-empty">Loading plans…</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <PlanCard plan={p} onInvest={handleInvest} />
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section id="contact" className="border-t border-white/10" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 grid lg:grid-cols-2 gap-24">
          <Reveal>
            <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-6">Begin the Conversation</p>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tighter">Talk to our desk.</h2>
            <div className="mt-12 space-y-8">
              <div>
                <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-white/35">Email</p>
                <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="text-lg text-white hover:text-[#D4AF37] transition-colors">{CONTACT.email}</a>
              </div>
              <div>
                <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-white/35">Investor Support</p>
                <p className="text-lg text-white">Registered investors can message our desk anytime from the dashboard support chat.</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-[#0A0A0A] border border-white/10 p-8 lg:p-12">
              <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-white/35 mb-8">Enquiry Form</p>
              <EnquiryForm plans={plans} />
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="border-t border-white/10" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 flex flex-wrap items-center justify-between gap-8">
          <div className="flex items-center gap-5">
            <img src="/logo-full.png" alt="MetrixWorks Service Private Limited" className="h-20 bg-white px-3 py-2" data-testid="footer-logo-image" />
            <div>
              <a href={`mailto:${CONTACT.email}`} className="text-sm text-[#D4AF37] hover:text-white transition-colors" data-testid="footer-email-link">{CONTACT.email}</a>
            </div>
          </div>
          <p className="font-accent text-[10px] tracking-[0.2em] uppercase text-white/30">
            © {new Date().getFullYear()} MetrixWorks Service Pvt Ltd. Investments are subject to market risk.
          </p>
          <Link to="/login" data-testid="footer-admin-link" className="font-accent text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-[#D4AF37] transition-colors border border-white/15 px-3 py-2">
            Admin Login
          </Link>
        </div>
      </footer>
    </div>
  );
}
